import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadRazorpayScript } from "@/components/site/MarketRegForm";
import { inr, CHECKOUT_EVENT } from "@/components/site/MeditationBits";

const EMPTY = { full_name: "", email: "", mobile: "", country_code: "+91" };

function ProductSummary({ product, includes }) {
  const isCol = product.kind === "collection";
  return (
    <div className="rounded-2xl bg-[#F3EFEA] p-4" data-testid="checkout-summary">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta">{isCol ? "Complete Collection" : product.theme}</div>
          <div className="font-serif text-xl text-[#2D2422] leading-tight mt-1" data-testid="checkout-product-name">{product.name}</div>
          {!isCol && product.subtitle && <div className="text-xs italic text-[#7E736E]">{product.subtitle}</div>}
        </div>
        <div className="text-right shrink-0">
          {isCol && product.combined_value_inr > product.price_inr && <div className="text-xs line-through text-[#9C928D]">{inr(product.combined_value_inr)}</div>}
          <div className="font-semibold text-xl text-[#2D2422]" data-testid="checkout-amount">{inr(product.price_inr)}</div>
        </div>
      </div>
      {isCol && includes.length > 0 && (
        <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[13px] text-[#3D3633]" data-testid="checkout-includes">
          <li className="col-span-2 text-xs text-[#7E736E]">Includes:</li>
          {includes.map(n => <li key={n} className="flex items-center gap-1.5"><Check size={13} className="text-terracotta shrink-0" />{n}</li>)}
        </ul>
      )}
      <div className="mt-2 text-xs text-[#7E736E]">One-time purchase</div>
    </div>
  );
}

export function MeditationCheckoutHost({ includes = [] }) {
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const open = (e) => {
      const p = e.detail;
      setProduct(p); setBusy(false);
      api.get("/public/meditations").then(({ data }) => {
        const fresh = [...data.meditations, data.collection].find(x => x && x.slug === p.slug);
        if (fresh) setProduct(cur => (cur && cur.slug === fresh.slug ? fresh : cur));
      }).catch(() => {});
    };
    window.addEventListener(CHECKOUT_EVENT, open);
    return () => window.removeEventListener(CHECKOUT_EVENT, open);
  }, []);
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const markStatus = (pid, status, reason = "") => api.post(`/public/meditation-orders/${pid}/status`, { status, reason }).catch(() => {});

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const { data: order } = await api.post("/public/meditation-orders", {
        product_slug: product.slug, ...form, source: params.get("utm_source") || params.get("source") || "Website",
        landing_page: window.location.pathname,
      });
      if (!(await loadRazorpayScript()) || !window.Razorpay) {
        markStatus(order.purchase_id, "failed", "checkout_script_unavailable");
        toast.error("Payment window could not load. Please check your connection and try again.");
        return setBusy(false);
      }
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: order.name, description: order.description, prefill: order.prefill, theme: { color: "#E07A5F" },
        handler: async (resp) => {
          try {
            await api.post("/public/meditation-orders/verify", { purchase_id: order.purchase_id, ...resp });
            setProduct(null);
            nav(`/meditations/purchase/${order.purchase_id}`);
          } catch { toast.error("We could not verify your payment. If money was deducted, please contact us — your reference is " + order.order_id); setBusy(false); }
        },
        modal: { ondismiss: () => { markStatus(order.purchase_id, "cancelled"); setBusy(false); toast.message("Payment cancelled. You have not been charged."); } },
      });
      rz.on("payment.failed", (r) => markStatus(order.purchase_id, "failed", r?.error?.description || ""));
      setProduct(null);
      setTimeout(() => { document.body.style.pointerEvents = ""; rz.open(); }, 350);
    } catch (err) { toast.error(formatErr(err)); setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => { if (!o && !busy) setProduct(null); }}>
      <DialogContent className="w-[94vw] sm:max-w-md p-5 sm:p-6 max-h-[94vh] overflow-y-auto" data-testid="meditation-checkout">
        {product && (
          <form onSubmit={pay} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-left">Your details</DialogTitle>
              <DialogDescription className="text-left text-xs">Secure checkout · No account needed</DialogDescription>
            </DialogHeader>
            <ProductSummary product={product} includes={includes} />
            <div className="space-y-1"><Label htmlFor="co-name">Name</Label>
              <Input id="co-name" required autoComplete="name" className="h-12 text-base" value={form.full_name} onChange={e => upd("full_name", e.target.value)} data-testid="checkout-name" /></div>
            <div className="space-y-1"><Label htmlFor="co-email">Email</Label>
              <Input id="co-email" type="email" required autoComplete="email" inputMode="email" className="h-12 text-base" value={form.email} onChange={e => upd("email", e.target.value)} data-testid="checkout-email" /></div>
            <div className="space-y-1"><Label htmlFor="co-mobile">Mobile / WhatsApp Number</Label>
              <div className="flex gap-2">
                <Input aria-label="Country code" className="h-12 w-20 text-base text-center" value={form.country_code} onChange={e => upd("country_code", e.target.value)} data-testid="checkout-country-code" />
                <Input id="co-mobile" type="tel" required autoComplete="tel-national" inputMode="numeric" className="h-12 text-base flex-1" value={form.mobile} onChange={e => upd("mobile", e.target.value)} data-testid="checkout-mobile" />
              </div></div>
            <button type="submit" disabled={busy} className="btn-primary w-full !py-4 text-base disabled:opacity-60" data-testid="checkout-pay">
              <Lock size={16} className="mr-2" />{busy ? "Opening secure payment…" : `Pay ${inr(product.price_inr)}`}
            </button>
            <p className="text-[11px] text-center text-[#9C928D]">Payments are processed securely by Razorpay.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
